import React from "react"
import { Provider } from "../../types/Providers"
import { Benefit } from "#/lib/carrot-api/types/Benefit"
import { useCompanyContext } from "#/pages/provider-finder/context/CompanyContextProvider"
import Settings from "#/pages/provider-finder/lib/Settings"
import { TieredNetworkChip } from "#/pages/provider-finder/components/results/TieredNetworkChip"
import { Box, Chip } from "@carrotfertility/carotene-core"
import { DoulaServicesChips } from "#/pages/provider-finder/components/results/DoulaServicesChips"
import { VirtualServicesTag } from "#/pages/provider-finder/components/results/VirtualServicesTag"
import { DirectPayChip } from "#/pages/provider-finder/components/results/DirectPayChip"
import { useGetProviderFinderNetworkCustomization } from "#/components/views/app/navigation/hooks/useGetProviderFinderNetworkCustomization"
import { useGetFundsAdministration } from "#/components/bank-details/hooks/useGetFundsAdministration"

interface ChipsProps {
  provider: Provider
  benefit: Benefit
}

function ProviderNetworkChip({ providerNetworkIds }: { providerNetworkIds?: string[] }) {
  const { data: providerFinderNetworkCustomization } = useGetProviderFinderNetworkCustomization()

  return (
    <>
      {providerFinderNetworkCustomization?.externalProviderNetworkId &&
        providerNetworkIds?.includes(providerFinderNetworkCustomization?.externalProviderNetworkId) && (
          <Chip size="small" color="info" label={providerFinderNetworkCustomization?.chipAndFilterText} />
        )}
    </>
  )
}

const CARROT_NETWORK_ID = 1

export function ProviderChips({ provider, benefit }: ChipsProps): JSX.Element | null {
  const { companyId } = useCompanyContext()
  const networkConfigForEnv = Settings.COMPANY_IDS_WITH_TIERED_PROVIDER_NETWORK

  const { networkIds, providerNetworkIds, doulaServices, isVirtual, acceptsDirectPay } = provider

  const fundsAdministration = useGetFundsAdministration()

  const hasCustomNetwork = networkIds.filter((networkId) => networkId !== CARROT_NETWORK_ID).length > 0
  const hasTieredNetwork = hasCustomNetwork && networkConfigForEnv.includes(companyId)
  const hasDoulaServices = doulaServices && doulaServices.length > 0
  const hasProviderNetwork = providerNetworkIds && providerNetworkIds.length > 0

  const showDirectPayChip = acceptsDirectPay && fundsAdministration.data?.reimbursement?.isCarrotDirectPayEnabled

  if (!(hasTieredNetwork || hasDoulaServices || isVirtual || showDirectPayChip || hasProviderNetwork)) {
    return null
  }

  return (
    <Box
      display="flex"
      flexDirection="row"
      gap={(theme) => theme.spacing(theme.tokens.spacing.xs)}
      sx={{ flexWrap: "wrap" }}
      id={"provider-chips"}
    >
      {hasTieredNetwork && <TieredNetworkChip networkIds={networkIds} benefit={benefit} />}
      {hasDoulaServices && <DoulaServicesChips doulaServices={doulaServices} />}
      {isVirtual && <VirtualServicesTag />}
      {showDirectPayChip && <DirectPayChip />}
      {hasProviderNetwork && <ProviderNetworkChip providerNetworkIds={providerNetworkIds} />}
    </Box>
  )
}
